import BigNumber from 'bignumber.js';
import { useCallback, useMemo, useState } from 'react';

import type { SigningRequestModification } from '@infinex-private/connect-types';
import type { ChainKey } from '@infinex/chain-config';
import type { SigningRequestMetadata } from '@infinex/connect-service-contract/client';
import { useAssetPriceQuery } from '@infinex/web-shared/api';
import { useUnifiedBalances } from '@infinex/web-shared/features/balances';

import { formatRawTokenAmount } from '../components/helpers/tokenAmountUtils';
import type { ApprovalEditState } from '../components/ValidationResultCard';
import type {
  NormalizedAssetApproval,
  NormalizedValidationResult,
} from '../components/ValidationResultCard/normalizeChainResponse';

BigNumber.set({ EXPONENTIAL_AT: 100 });

type UseApprovalEditingParams = {
  metadata: SigningRequestMetadata | undefined;
  chainKey: ChainKey;
  isTransaction: boolean;
  isHardwareWallet: boolean;
  normalizedValidationResult: NormalizedValidationResult | null;
  modifications: SigningRequestModification[];
  setModification: (modification: SigningRequestModification) => void;
};

/**
 * Derives the per-token USD rate from blockaid's own approval data.
 * Returns null for unlimited approvals or when the data can't be parsed.
 */
function deriveBlockaidTokenRate(
  approval: NormalizedAssetApproval
): number | null {
  if (approval.amount === 'Unlimited' || approval.usdValue === 'Unlimited') {
    return null;
  }

  const cleanAmount = BigNumber(approval.amount.replace(/,/g, ''));
  const cleanUsd = BigNumber(approval.usdValue.replace(/,/g, ''));

  if (cleanAmount.isNaN() || cleanUsd.isNaN() || cleanAmount.isZero()) {
    return null;
  }

  return cleanUsd.dividedBy(cleanAmount).toNumber();
}

export function useApprovalEditing({
  metadata,
  chainKey,
  isTransaction,
  isHardwareWallet,
  normalizedValidationResult,
  modifications,
  setModification,
}: UseApprovalEditingParams) {
  const [editingApproval, setEditingApproval] =
    useState<NormalizedAssetApproval | null>(null);

  const isApprovalEditable =
    isTransaction &&
    !isHardwareWallet &&
    metadata?.input?.type === 'evm:userOperation';

  const approvalEditState = useMemo((): ApprovalEditState => {
    const modifiedAmounts = new Map<string, string>();

    for (const mod of modifications) {
      if (mod.type !== 'evm:approval') continue;

      const key = `${mod.token.toLowerCase()}:${mod.spender.toLowerCase()}`;
      const approval = normalizedValidationResult?.assetApprovals.find(
        (a) =>
          a.asset.type !== 'native' &&
          a.asset.address?.toLowerCase() === mod.token.toLowerCase() &&
          a.spenderAddress.toLowerCase() === mod.spender.toLowerCase()
      );

      if (approval) {
        const formatted = formatRawTokenAmount(
          BigInt(mod.amount),
          approval.asset.decimals
        );
        modifiedAmounts.set(key, formatted);
      }
    }

    return { modifiedAmounts };
  }, [modifications, normalizedValidationResult]);

  const handleApprovalEditPress = useCallback(
    (approval: NormalizedAssetApproval) => {
      setEditingApproval(approval);
    },
    []
  );

  /** Extracts the raw approval amount from blockaid simulation data */
  const editingApprovalRawAmount = useMemo((): bigint => {
    if (!editingApproval || !metadata?.blockaidValidationResult) {
      return 0n;
    }

    // Blockaid types aren't fully typed in the shared package, narrow manually
    const blockaidResult = metadata.blockaidValidationResult as {
      simulation?: {
        account_summary?: {
          exposures?: Array<{
            asset: { address?: string };
            spenders?: Record<string, { approval?: string }>;
          }>;
        };
      };
    };

    const exposures =
      blockaidResult?.simulation?.account_summary?.exposures ?? [];

    for (const exposure of exposures) {
      if (
        editingApproval.asset.type !== 'native' &&
        exposure.asset.address?.toLowerCase() ===
          editingApproval.asset.address?.toLowerCase()
      ) {
        const spenderData =
          exposure.spenders?.[editingApproval.spenderAddress.toLowerCase()] ??
          exposure.spenders?.[editingApproval.spenderAddress];
        if (spenderData?.approval) {
          try {
            return BigInt(spenderData.approval);
          } catch {
            // Fall through to parsing from display amount
          }
        }
      }
    }

    if (editingApproval.amount !== 'Unlimited') {
      try {
        const cleanAmount = editingApproval.amount.replace(/,/g, '');
        return BigInt(
          BigNumber(cleanAmount)
            .shiftedBy(editingApproval.asset.decimals)
            .integerValue(BigNumber.ROUND_FLOOR)
            .toFixed(0)
        );
      } catch {
        // Fall through to max uint256
      }
    }

    return BigInt(
      '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
    );
  }, [editingApproval, metadata]);

  const handleApprovalEditConfirm = useCallback(
    (newRawAmount: string) => {
      if (!editingApproval || editingApproval.asset.type === 'native') {
        return;
      }

      setModification({
        type: 'evm:approval',
        token: editingApproval.asset.address!,
        spender: editingApproval.spenderAddress,
        amount: newRawAmount,
      });

      setEditingApproval(null);
    },
    [editingApproval, setModification]
  );

  const { tokenBalances } = useUnifiedBalances({ chain: [chainKey] });
  const editingApprovalUserBalance = useMemo(() => {
    if (!editingApproval || !tokenBalances) {
      return { formatted: undefined, raw: undefined };
    }

    const targetAddress = editingApproval.asset.address?.toLowerCase();
    const tokenBalance = tokenBalances.find(
      (balance) =>
        balance.chain === chainKey &&
        balance.assetAddress.toLowerCase() === targetAddress
    );

    if (!tokenBalance) {
      return { formatted: undefined, raw: undefined };
    }

    const formatted = tokenBalance.decimalBalance.toString();

    try {
      return {
        formatted,
        raw: BigInt(tokenBalance.rawBalance.toFixed(0)),
      };
    } catch {
      return { formatted, raw: undefined };
    }
  }, [editingApproval, tokenBalances, chainKey]);

  // Derive rate from blockaid's own data when possible (more reliable than a
  // separate price API call, which may not recognise aTokens / wrapper tokens).
  const blockaidDerivedRate = useMemo(
    () => (editingApproval ? deriveBlockaidTokenRate(editingApproval) : null),
    [editingApproval]
  );

  // Fall back to our price API only for unlimited approvals where blockaid
  // can't give us a rate.
  const needsPriceApiFallback =
    !!editingApproval &&
    editingApproval.asset.type !== 'native' &&
    blockaidDerivedRate === null;

  const { data: editingApprovalTokenPrice, isLoading: isTokenPriceLoading } =
    useAssetPriceQuery(
      {
        chain: chainKey,
        address: editingApproval?.asset.address ?? '',
      },
      {
        enabled: needsPriceApiFallback,
      }
    );

  const editingApprovalTokenUsdRate =
    blockaidDerivedRate ?? editingApprovalTokenPrice?.usd?.rate;

  return {
    isApprovalEditable,
    approvalEditState,
    editingApproval,
    editingApprovalRawAmount,
    editingApprovalUserBalance,
    editingApprovalTokenUsdRate,
    isTokenPriceLoading: needsPriceApiFallback && isTokenPriceLoading,
    handleApprovalEditPress,
    handleApprovalEditConfirm,
    dismissEditDialog: useCallback(() => setEditingApproval(null), []),
  };
}
